import type {
  OrgProjectContextOption,
  OrgProjectFallbackContext,
  OrgProjectInvalidReason,
  OrgProjectLoader,
  OrgProjectResolution,
  RequestedOrgProjectContext,
} from "./types";

export function pickActiveId(
  items: OrgProjectContextOption[],
  preferredId?: string | null
): string | null {
  if (items.length === 0) {
    return null;
  }

  if (preferredId && items.some((item) => item.id === preferredId)) {
    return preferredId;
  }

  return items.find((item) => item.isDefault)?.id ?? items[0]?.id ?? null;
}

type RecoveryPayload = {
  organizationId: string;
  projectId: string;
  projects: OrgProjectContextOption[];
} | null;

function toFallbackContext(
  recovery: RecoveryPayload
): OrgProjectFallbackContext | null {
  return recovery
    ? {
        organizationId: recovery.organizationId,
        projectId: recovery.projectId,
      }
    : null;
}

/**
 * Resolves org/project route params using the shared selection policy.
 * Does not perform HTTP or caching — inject that via {@link OrgProjectLoader}.
 */
export async function resolveOrgProjectContext(
  loader: OrgProjectLoader,
  requested?: RequestedOrgProjectContext
): Promise<OrgProjectResolution> {
  const organizations = await loader.listOrganizations();

  const buildEmpty = (
    projects: OrgProjectContextOption[] = []
  ): OrgProjectResolution => ({
    outcome: "empty",
    organizations,
    projects,
    fallbackContext: null,
  });

  const buildInvalid = (
    reason: OrgProjectInvalidReason,
    recovery: RecoveryPayload,
    projects: OrgProjectContextOption[] = []
  ): OrgProjectResolution => ({
    outcome: "invalid",
    reason,
    organizations,
    projects,
    fallbackContext: toFallbackContext(recovery),
  });

  const buildRedirect = (
    recovery: NonNullable<RecoveryPayload>
  ): OrgProjectResolution => ({
    outcome: "redirect",
    organizationId: recovery.organizationId,
    projectId: recovery.projectId,
    organizations,
    projects: recovery.projects,
    fallbackContext: {
      organizationId: recovery.organizationId,
      projectId: recovery.projectId,
    },
  });

  const buildValid = (
    orgId: string,
    projId: string,
    projects: OrgProjectContextOption[]
  ): OrgProjectResolution => ({
    outcome: "valid",
    organizationId: orgId,
    projectId: projId,
    organizations,
    projects,
    fallbackContext: null,
  });

  const resolveDefaultRecovery = async (): Promise<RecoveryPayload> => {
    const organizationId = pickActiveId(organizations);
    if (!organizationId) {
      return null;
    }
    const projects = await loader.listProjects(organizationId);
    const projectId = pickActiveId(projects);
    if (!projectId) {
      return null;
    }
    return { organizationId, projectId, projects };
  };

  /**
   * Prefer staying in `orgId` when it's valid. Only fall back to the
   * global default org if no project can be picked for the given org.
   */
  const resolveRecoveryForOrg = async (
    orgId: string,
    knownProjects?: OrgProjectContextOption[]
  ): Promise<RecoveryPayload> => {
    const projects = knownProjects ?? (await loader.listProjects(orgId));
    const projectId = pickActiveId(projects);
    if (projectId) {
      return { organizationId: orgId, projectId, projects };
    }
    return resolveDefaultRecovery();
  };

  if (organizations.length === 0) {
    return buildEmpty();
  }

  const urlOrgId = requested?.organizationId;
  const urlProjectId = requested?.projectId;

  if (!urlOrgId) {
    const recovery = await resolveDefaultRecovery();
    if (!recovery) {
      return buildEmpty();
    }
    return buildRedirect(recovery);
  }

  if (!organizations.some((o) => o.id === urlOrgId)) {
    const recovery = await resolveDefaultRecovery();
    return buildInvalid(
      "unknown_organization_id",
      recovery,
      recovery?.projects ?? []
    );
  }

  const projects = await loader.listProjects(urlOrgId);

  if (!urlProjectId) {
    return buildInvalid(
      "missing_project_id",
      await resolveRecoveryForOrg(urlOrgId, projects),
      projects
    );
  }

  if (!projects.some((p) => p.id === urlProjectId)) {
    return buildInvalid(
      "unknown_project_id",
      await resolveRecoveryForOrg(urlOrgId, projects),
      projects
    );
  }

  return buildValid(urlOrgId, urlProjectId, projects);
}
